import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Eye, Lock, Briefcase, Cloud, Users, Truck, ChevronRight, Loader2, ArrowLeft, Settings, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  lock: Lock,
  briefcase: Briefcase,
  cloud: Cloud,
  users: Users,
  truck: Truck,
};

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2">
          <Eye className="w-6 h-6 text-brand" strokeWidth={2} />
          <span className="font-heading font-black text-lg tracking-tight text-zinc-900">ContractEye</span>
        </Link>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link to="/dashboard" className="font-body text-sm text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="templates-nav-dashboard">Dashboard</Link>
              <Link to="/settings" className="p-2 hover:bg-zinc-100 transition-colors"><Settings className="w-4 h-4 text-zinc-600" /></Link>
              <button onClick={async () => { await logout(); navigate('/'); }} className="p-2 hover:bg-zinc-100 transition-colors"><LogOut className="w-4 h-4 text-zinc-600" /></button>
            </>
          ) : (
            <>
              <Link to="/login" className="font-body text-sm text-zinc-600 hover:text-zinc-900" data-testid="templates-nav-login">Log In</Link>
              <Link to="/signup" className="bg-brand text-white px-5 py-2 font-body font-medium text-sm transition-colors" data-testid="templates-nav-signup">Get Started</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function RiskPreviewBadge({ score }) {
  let bg;
  if (score >= 7) bg = 'bg-rose-100 text-rose-700 border-rose-200';
  else if (score >= 4) bg = 'bg-amber-100 text-amber-700 border-amber-200';
  else bg = 'bg-emerald-100 text-emerald-700 border-emerald-200';
  return <span className={`${bg} border font-mono text-xs font-bold px-2 py-0.5`}>{score}/10</span>;
}

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/templates`);
        setTemplates(res.data.templates || []);
      } catch {
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="templates-page">
      <Navbar />
      <main className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <Link to="/dashboard" className="flex items-center gap-1 font-body text-sm text-zinc-500 hover:text-zinc-700 mb-6" data-testid="templates-back">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>

          <div className="mb-10">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-2">Template Library</p>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-zinc-900 mb-3">
              Pre-Built Analysis Profiles
            </h1>
            <p className="font-body text-base text-zinc-600 max-w-2xl leading-relaxed">
              Explore sample contract analyses to understand what ContractEye detects for each contract type. Each template highlights the key risk areas and focus points specific to that agreement type.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {templates.map((t) => {
              const Icon = ICONS[t.icon] || Lock;
              return (
                <button
                  key={t.slug}
                  onClick={() => navigate(`/templates/${t.slug}`)}
                  className="bg-white p-6 text-left hover:bg-zinc-50 transition-colors group"
                  data-testid={`template-card-${t.slug}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5 text-brand" strokeWidth={1.5} />
                      <span className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 font-body">{t.category}</span>
                    </div>
                    <RiskPreviewBadge score={t.risk_preview} />
                  </div>
                  <h3 className="font-heading font-bold text-base text-zinc-900 mb-1.5 group-hover:text-brand transition-colors">{t.title}</h3>
                  <p className="font-body text-xs text-zinc-500 leading-relaxed mb-4">{t.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {t.focus_areas.slice(0, 3).map((f, i) => (
                      <span key={i} className="bg-zinc-100 text-zinc-600 px-2 py-0.5 text-xs font-body">{f}</span>
                    ))}
                    {t.focus_areas.length > 3 && (
                      <span className="text-zinc-400 text-xs font-body">+{t.focus_areas.length - 3} more</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-brand font-body font-medium group-hover:gap-2 transition-all">
                    View Analysis <ChevronRight className="w-3 h-3" />
                  </div>
                </button>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-10 bg-white border border-zinc-200 p-8 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="templates-cta">
            <div>
              <h3 className="font-heading font-bold text-lg text-zinc-900 mb-1">Ready to scan your own contract?</h3>
              <p className="font-body text-sm text-zinc-500">Upload a PDF/DOCX or paste text to get a custom risk analysis.</p>
            </div>
            <Link to="/dashboard" className="bg-brand text-white hover:bg-brand-hover px-8 py-3 font-body font-medium text-sm transition-colors whitespace-nowrap" data-testid="templates-cta-btn">
              Go to Dashboard
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
